/**
 * Ingredients Formatter
 * Parses multilingual ingredient text and shows only the user's language.
 * Also emphasizes allergens (UPPERCASE words) in bold per EU 1169/2011.
 *
 * Language markers:
 * - DA/SE/NO or DK/SE/NO = Danish/Swedish/Norwegian (Scandinavian)
 * - GB or EN = English
 * - DE = German
 * - NL = Dutch
 * - FR = French
 * - Text before first marker = product description (always shown)
 */

(function() {
  'use strict';

  // Map browser/Shopify locales to ingredient language markers
  const localeToMarker = {
    'da': ['DA', 'DK'],
    'da-DK': ['DA', 'DK'],
    'sv': ['SE', 'DA'],  // Swedish falls back to Scandinavian section
    'sv-SE': ['SE', 'DA'],
    'nb': ['NO', 'DA'],  // Norwegian falls back to Scandinavian section
    'nb-NO': ['NO', 'DA'],
    'no': ['NO', 'DA'],
    'nn': ['NO', 'DA'],
    'de': ['DE'],
    'de-DE': ['DE'],
    'de-AT': ['DE'],
    'de-CH': ['DE'],
    'nl': ['NL'],
    'nl-NL': ['NL'],
    'nl-BE': ['NL'],
    'fr': ['FR'],
    'fr-FR': ['FR'],
    'fr-BE': ['FR'],
    'fr-CH': ['FR'],
    'en': ['GB', 'EN'],
    'en-GB': ['GB', 'EN'],
    'en-US': ['GB', 'EN'],
    'en-AU': ['GB', 'EN']
  };

  function getUserLanguage() {
    // First check Shopify's locale
    const shopifyLocale = window.Shopify?.locale || document.documentElement.lang;
    if (shopifyLocale && localeToMarker[shopifyLocale]) {
      return localeToMarker[shopifyLocale];
    }

    // Check browser language
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang) {
      // Try exact match first
      if (localeToMarker[browserLang]) {
        return localeToMarker[browserLang];
      }
      // Try base language (e.g., 'da' from 'da-DK')
      const baseLang = browserLang.split('-')[0];
      if (localeToMarker[baseLang]) {
        return localeToMarker[baseLang];
      }
    }

    // Default to English
    return ['GB', 'EN'];
  }

  /**
   * Escape HTML special characters to prevent XSS when using innerHTML.
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Emphasize allergens in ingredients text.
   *
   * Per EU 1169/2011, allergens must be visually highlighted. Simply's
   * convention (and Summerbird's, etc.) is to write allergens in UPPERCASE
   * inside the ingredient list. We detect any run of 2+ uppercase letters
   * (Latin + Scandinavian ÆØÅ + German ÄÖÜ) and wrap them in <strong>.
   *
   * Note: We deliberately do NOT use \b (word boundary) because JavaScript's
   * \b is ASCII-only — it doesn't recognize ÆØÅÄÖÜ as word characters,
   * which causes "MÆLKS" inside "sødMÆLKSpulver" to only partially match
   * (the ÆLKS part) and standalone "ÆG" after a space to not match at all.
   * Without \b, we correctly catch the full uppercase runs in compound words
   * like sødMÆLKS, helMJÖLKS, SOJAlecithin.
   *
   * Returns an HTML string. Caller must use innerHTML.
   */
  function emphasizeAllergens(text) {
    // First escape HTML to prevent injection
    const escaped = escapeHtml(text);
    // Wrap any run of 2+ uppercase letters (Latin + Scandinavian + German) in <strong>
    return escaped.replace(/[A-ZÆØÅÄÖÜ]{2,}/g, '<strong>$&</strong>');
  }

  function parseIngredients(text) {
    // Clean the text - normalize whitespace but preserve structure
    const cleanText = text.replace(/\s+/g, ' ').trim();

    // Language marker pattern - matches DA/SE/NO, GB, DE, NL, FR etc.
    // Must be followed by ingredient-like content
    const markerRegex = /(DA\/SE\/NO|DK\/SE\/NO|DA\/NO\/SE|GB|EN|DE|NL|FR)\s+/g;

    const sections = [];

    // Find all marker positions
    const markers = [];
    let match;
    while ((match = markerRegex.exec(cleanText)) !== null) {
      // Check if this looks like a real language marker
      // It should be at the start, after a period, or after a newline-like break
      const beforeMarker = cleanText.substring(Math.max(0, match.index - 3), match.index);
      const isAtStart = match.index === 0;
      const isAfterPeriod = /\.\s*$/.test(beforeMarker);
      const isAfterBreak = /[\n\r]\s*$/.test(beforeMarker);

      // Check what comes after the marker
      const afterMarker = cleanText.substring(match.index + match[0].length, match.index + match[0].length + 100);

      // Check if the section contains "Ingredienser:", "Ingredients:", etc. somewhere within it
      const containsIngredientWord = /(Ingredienser|Ingredients|Zutaten|Ingrediënten|Ingrédients)\s*:/i.test(afterMarker);

      // Also accept if it starts with a capital letter (product name) and we have multiple language markers
      const startsWithCapital = /^[A-ZÆØÅÄÖÜ]/i.test(afterMarker);

      if (containsIngredientWord || (startsWithCapital && (isAtStart || isAfterPeriod))) {
        markers.push({
          marker: match[1].toUpperCase(),
          index: match.index,
          length: match[0].length
        });
      }
    }

    if (markers.length === 0) {
      // No language markers found
      return { intro: '', sections: [{ lang: 'all', text: cleanText }] };
    }

    // Extract each language section
    for (let i = 0; i < markers.length; i++) {
      const current = markers[i];
      const next = markers[i + 1];

      const startIndex = current.index + current.length;
      const endIndex = next ? next.index : cleanText.length;

      let sectionText = cleanText.substring(startIndex, endIndex).trim();

      // Clean up trailing period and whitespace
      sectionText = sectionText.replace(/\.\s*$/, '').trim();

      // Normalize compound markers (DA/SE/NO -> DA)
      let normalizedMarker = current.marker;
      if (normalizedMarker.includes('/')) {
        normalizedMarker = normalizedMarker.split('/')[0];
      }

      sections.push({
        lang: normalizedMarker,
        text: sectionText
      });
    }

    return { intro: '', sections };
  }

  function formatIngredientsContent(content) {
    const text = content.textContent || '';
    const parsed = parseIngredients(text);

    if (parsed.sections.length <= 1 && !parsed.intro) {
      // No multilingual content detected
      return null;
    }

    const userLangMarkers = getUserLanguage();

    // Find the section for user's language
    let selectedSection = null;
    for (const marker of userLangMarkers) {
      selectedSection = parsed.sections.find(s => s.lang === marker || s.lang === 'all');
      if (selectedSection) break;
    }

    // Fall back to GB/EN if no match
    if (!selectedSection) {
      selectedSection = parsed.sections.find(s => s.lang === 'GB' || s.lang === 'EN');
    }

    // Last resort: first section
    if (!selectedSection && parsed.sections.length > 0) {
      selectedSection = parsed.sections[0];
    }

    if (!selectedSection) {
      return null;
    }

    // Build formatted output
    const container = document.createElement('div');
    container.className = 'ingredients-formatted';

    // Get the ingredients text
    let ingredientsText = selectedSection.text;

    // Language labels for display (normal case, not uppercase)
    const langLabels = {
      'DA': 'Danmark / Sverige / Norge',
      'DK': 'Danmark / Sverige / Norge',
      'GB': 'English',
      'EN': 'English',
      'DE': 'Deutsch',
      'NL': 'Nederlands',
      'FR': 'Français'
    };

    // Check if text has "Ingredienser:" or similar - extract and clean up
    const prefixMatch = ingredientsText.match(/^(.+?)\.\s*(Ingredienser|Ingredients|Zutaten|Ingrediënten|Ingrédients)\s*:\s*/i);
    const directPrefixMatch = ingredientsText.match(/^(Ingredienser|Ingredients|Zutaten|Ingrediënten|Ingrédients)\s*:\s*/i);

    let productDesc = '';

    if (prefixMatch) {
      // Has product description before ingredients (e.g., "Marcipan m/ chokoladeátræk. Ingredienser: ...")
      productDesc = prefixMatch[1];
      ingredientsText = ingredientsText.substring(prefixMatch[0].length);
    } else if (directPrefixMatch) {
      // Starts directly with "Ingredienser: ..."
      ingredientsText = ingredientsText.substring(directPrefixMatch[0].length);
    }

    // Add language badge
    const langBadge = document.createElement('span');
    langBadge.className = 'ingredients-lang';
    langBadge.textContent = langLabels[selectedSection.lang] || selectedSection.lang;
    container.appendChild(langBadge);

    // Add product description if present (textContent - no allergen emphasis needed here)
    if (productDesc) {
      const desc = document.createElement('p');
      desc.className = 'ingredients-desc';
      desc.textContent = productDesc;
      container.appendChild(desc);
    }

    // Add ingredients list with allergen emphasis (uppercase runs wrapped in <strong>)
    const list = document.createElement('p');
    list.className = 'ingredients-list';
    list.innerHTML = emphasizeAllergens(ingredientsText);
    container.appendChild(list);

    return container;
  }

  function formatIngredientsInAccordions() {
    // Find all accordion content (with or without prose class)
    const accordionContents = document.querySelectorAll('.accordion__content');

    accordionContents.forEach(content => {
      // Skip if already formatted
      if (content.querySelector('.ingredients-formatted')) {
        return;
      }

      // Skip if this is nutrition info (handled by nutrition-formatter.js)
      if (content.querySelector('.nutrition-formatted')) {
        return;
      }

      const text = content.textContent || '';

      // Check if this looks like multilingual ingredients
      // Must have multiple language markers like DA/SE/NO, GB, DE, NL with ingredient keywords
      const hasMultipleMarkers = (text.match(/(DA\/SE\/NO|DK\/SE\/NO|GB|DE|NL|FR)\s+/g) || []).length >= 2;
      const hasIngredientKeyword = /(Ingredienser|Ingredients|Zutaten|Ingrediënten|Ingrédients)\s*:/i.test(text);
      const hasLanguageMarker = hasMultipleMarkers && hasIngredientKeyword;

      if (hasLanguageMarker) {
        const formatted = formatIngredientsContent(content);

        if (formatted) {
          // Store original content as hidden fallback
          const existingContent = content.innerHTML;
          content.innerHTML = '';
          content.appendChild(formatted);

          const raw = document.createElement('div');
          raw.className = 'ingredients-raw';
          raw.style.display = 'none';
          raw.innerHTML = existingContent;
          content.appendChild(raw);
        }
      }
    });
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', formatIngredientsInAccordions);
  } else {
    formatIngredientsInAccordions();
  }

  // Also run when accordions are opened (for lazy-loaded content)
  document.addEventListener('toggle', function(e) {
    if (e.target.matches('details')) {
      setTimeout(formatIngredientsInAccordions, 50);
    }
  }, true);
})();
